import { CheckRounded } from "@mui/icons-material";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useIsMobile } from "../../hooks/useMediaQuery";
import Modal from "./Modal";
import styles from "./css/ActionMenu.module.css";

export interface ActionMenuItem {
  label: string;
  icon?: ReactNode;
  selected?: boolean;
  destructive?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

interface ActionMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  items: ActionMenuItem[];
  onClose: () => void;
  // 모바일 바텀시트에서 보여줄 제목
  title?: string;
  // 항목을 고르면 메뉴를 자동으로 닫을지 여부
  closeOnSelect?: boolean;
}

// 데스크톱은 버튼 아래에 뜨는 팝오버, 모바일은 바텀시트로 보여준다.
export default function ActionMenu(props: ActionMenuProps) {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <Modal
        open={props.open}
        onClose={props.onClose}
        title={props.title}
        placement="sheet"
      >
        <MenuList
          items={props.items}
          onClose={props.onClose}
          closeOnSelect={props.closeOnSelect ?? true}
          large
        />
      </Modal>
    );
  }
  return <Popover {...props} />;
}

function MenuList({
  items,
  onClose,
  closeOnSelect,
  large = false,
}: {
  items: ActionMenuItem[];
  onClose: () => void;
  closeOnSelect: boolean;
  large?: boolean;
}) {
  return (
    <ul role="menu" className={styles.list}>
      {items.map((item, index) => (
        <li key={index} role="none">
          <button
            type="button"
            role="menuitem"
            disabled={item.disabled}
            className={classNames(styles.item, {
              [styles.large]: large,
              [styles.destructive]: item.destructive,
              [styles.selected]: item.selected,
            })}
            onClick={() => {
              if (closeOnSelect) onClose();
              item.onSelect();
            }}
          >
            {item.icon && <span className={styles.icon}>{item.icon}</span>}
            <span className={styles.label}>{item.label}</span>
            {item.selected && <CheckRounded className={styles.check} />}
          </button>
        </li>
      ))}
    </ul>
  );
}

function Popover({
  open,
  anchorEl,
  items,
  onClose,
  closeOnSelect = true,
}: ActionMenuProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  useLayoutEffect(() => {
    if (!open || !anchorEl || !popoverRef.current) return;
    const gap = 6;
    const margin = 8;
    const anchor = anchorEl.getBoundingClientRect();
    const popover = popoverRef.current.getBoundingClientRect();

    let left = anchor.left;
    if (left + popover.width > window.innerWidth - margin) {
      left = anchor.right - popover.width;
    }
    let top = anchor.bottom + gap;
    if (top + popover.height > window.innerHeight - margin) {
      top = anchor.top - popover.height - gap;
    }
    setPosition({ top: Math.max(margin, top), left: Math.max(margin, left) });
  }, [open, anchorEl, items.length]);

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target) || anchorEl?.contains(target)) {
        return;
      }
      onClose();
    }
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", onClose);
    };
  }, [open, anchorEl, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          ref={popoverRef}
          className={styles.popover}
          style={position}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.12 }}
        >
          <MenuList
            items={items}
            onClose={onClose}
            closeOnSelect={closeOnSelect}
          />
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
